import { Button, Checkbox, FormControlLabel, IconButton, Stack, Typography } from '@mui/material';
import {
  ApiError,
  CHANGE_LAST_SCHOOL,
  useDeleteSchoolMutation,
  useMutationFromKey,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useNotifications } from '@schooly/components/notifications';
import { SchoolUserRole } from '@schooly/constants';
import { ArrowRightIcon, CrossIcon, Spin } from '@schooly/style';
import { FC, useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import logo from '../../../../assets/images/schooly-logo.svg';
import { FormTextField } from '../../../../components/uikit-components/FormTextField/FormTextField';
import {
  ModalConfirm,
  ModalContent,
  ModalFooter,
  ModalMain,
  ModalOpaque,
} from '../../../../components/uikit-components/Modal/Modal.styled';
import { ModalHeader } from '../../../../components/uikit-components/Modal/ModalHeader';
import { useRouter } from '../../../../context/router/useRouter';
import { useSchool } from '../../../../hooks/useSchool';
import { SchoolSettingsLayout } from '../../SchoolSettingsLayout';

enum State {
  Confirm,
  EnterName,
  Deleted,
}

export const SchoolGeneralDeleteSchoolModal: FC = () => {
  const { $t } = useIntl();
  const { currentUser } = useAuth();
  const { currentSchool, schoolId, onSelectSchool } = useSchool();
  const router = useRouter();
  const { showError } = useNotifications();
  const [isConfirmed, _setConfirmed] = useState(false);
  const [state, setState] = useState<State>(State.Confirm);
  const [name, _setName] = useState('');
  const [hasError, setError] = useState(false);
  const handleClose = router.goBack;
  const deleteSchool = useDeleteSchoolMutation();
  const { isLoading: isSchoolChanging } = useMutationFromKey([CHANGE_LAST_SCHOOL]);

  const toggleConfirmed = useCallback(() => {
    hasError && setError(false);

    _setConfirmed((c) => !c);
  }, [hasError]);
  const setName = useCallback(
    (v: string) => {
      hasError && setError(false);

      _setName(v);
    },
    [hasError],
  );

  const isNameCorrect = name === currentSchool?.name;

  const handleNext = useCallback(async () => {
    if (!schoolId) return;

    switch (state) {
      case State.Confirm:
        if (!isConfirmed) {
          setError(true);
          break;
        }

        setError(false);
        setState(State.EnterName);
        break;
      case State.EnterName:
        if (!isNameCorrect) {
          setError(true);
          break;
        }

        try {
          setError(false);
          await deleteSchool.mutateAsync(schoolId);
          setState(State.Deleted);
        } catch (err) {
          showError(err as ApiError);
        }

        break;
      case State.Deleted:
        break;
      default:
        return;
    }
  }, [schoolId, state, isConfirmed, isNameCorrect, deleteSchool, showError]);

  const renderFormContent = () => {
    switch (state) {
      case State.Confirm:
        return (
          <>
            <Typography variant="h3" color="text.primary">
              <FormattedMessage id="school-delete-ThisActionWillDelete" />
            </Typography>
            <Stack gap={1} mt={1}>
              <Typography variant="h3" color="text.primary">
                <FormattedMessage id="school-delete-SchoolInformation" />
              </Typography>
              <Typography variant="h3" color="text.primary">
                <FormattedMessage id="school-delete-StudentInformation" />
              </Typography>
              <Typography variant="h3" color="text.primary">
                <FormattedMessage id="school-delete-StaffInformation" />
              </Typography>
            </Stack>
            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Checkbox
                  onChange={toggleConfirmed}
                  checked={isConfirmed}
                  color={hasError ? 'error' : undefined}
                />
              }
              label={
                <>
                  <Typography variant="h3">
                    <FormattedMessage
                      id="school-delete-IConfirmThat"
                      values={{ schoolName: currentSchool?.name }}
                    />
                  </Typography>
                </>
              }
            />
            {hasError && (
              <Typography variant="body2" color="error" ml={3.25}>
                <FormattedMessage id="school-delete-errorFieldRequired" />
              </Typography>
            )}
          </>
        );
      case State.EnterName:
        return (
          <>
            <Typography variant="h3" color="text.primary">
              <FormattedMessage id="school-delete-PleaseEnterName" />
            </Typography>
            <FormTextField
              onChange={(e) => setName(e.target.value)}
              value={name}
              label={$t({ id: 'school-delete-SchoolName' })}
              required
              fullWidth
              canClear
              error={hasError}
              helperText={hasError ? $t({ id: 'school-delete-errorSchoolName' }) : undefined}
            />
          </>
        );
      default:
        return null;
    }
  };

  if (state === State.Deleted)
    return (
      <ModalOpaque open>
        <ModalContent active>
          <img src={logo} width={110} alt="Schooly" />
          <Typography variant="h1" my={2}>
            <FormattedMessage
              id="school-delete-SchoolRemoved"
              values={{ schoolName: currentSchool?.name }}
            />
          </Typography>
          <Stack alignItems="flex-end">
            <Button
              onClick={async () => {
                const schoolRelation = currentUser?.school_relations.find(
                  (s) => s.role === SchoolUserRole.Staff,
                );
                if (schoolRelation) {
                  await onSelectSchool(schoolRelation.school.id);
                }
                window.location.href = window.location.origin;
              }}
              disabled={isSchoolChanging}
            >
              <FormattedMessage id="school-delete-BackToMain" />
            </Button>
          </Stack>
        </ModalContent>
      </ModalOpaque>
    );

  return (
    <SchoolSettingsLayout>
      <ModalConfirm open fullWidth onClose={handleClose}>
        <ModalHeader
          multiline
          title={$t({ id: 'school-delete-AreYouSure' }, { schoolName: currentSchool?.name })}
          withBorderBottom={false}
          active
        >
          <IconButton disabled={deleteSchool.isLoading} onClick={handleClose}>
            <CrossIcon />
          </IconButton>
        </ModalHeader>
        <ModalMain>
          <ModalContent active>
            <Stack gap={1} mt={-2}>
              {renderFormContent()}
            </Stack>
          </ModalContent>
        </ModalMain>
        <ModalFooter withBorderTop={false} active>
          <Button variant="outlined" disabled={deleteSchool.isLoading} onClick={handleClose}>
            <FormattedMessage id="action-Cancel" />
          </Button>
          <Button
            color="error"
            onClick={handleNext}
            disabled={deleteSchool.isLoading}
            endIcon={deleteSchool.isLoading ? <Spin /> : <ArrowRightIcon />}
          >
            <FormattedMessage id="action-Continue" />
          </Button>
        </ModalFooter>
      </ModalConfirm>
    </SchoolSettingsLayout>
  );
};
